/**
 * ThemeEditorDialogV2 - Full-screen dialog for editing V2 theme settings
 *
 * V2 counterpart of ThemeEditorDialog. Wraps ThemeV2EditorView (carousel + settings panel)
 * inside a full-screen Dialog for use from the canvas template panel.
 *
 * Converts OnboardingThemeSettingsV2 ↔ ThemeV2 for editing.
 *
 * Features:
 * - Back/Save/Done header buttons
 * - Nested V2 screen editor dialog (OnboardingScreenEditorDialogV2)
 * - Loading state during save
 * - Auto-propagation of colors/fonts via ThemeV2EditorView
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  Box,
  Button,
  Typography,
  CircularProgress,
  Stack,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ThemeV2EditorView } from '@modules/themes/components/ThemeV2EditorView';
import { GenerateThemeFromImageDialog } from '@modules/themes/components/GenerateThemeFromImageDialog';
import { GeneratedThemeMetadataPanel } from '@modules/themes/components/GeneratedThemeMetadataPanel';
import { OnboardingScreenEditorDialogV2 } from '@modules/reactFlow/components/onboarding/OnboardingScreenEditorDialogV2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import type { CreateThemeV2Input, ThemeV2 } from '@modules/themes/types/themesV2.types';
import { templateTileKey } from '@modules/themes/mappers/templateIdentity';
import type { StepNode } from '@segmently/schema-models/v2';
import {
  createEditableThemeV2FromDraft,
  onboardingSettingsV2ToThemeV2,
  themeV2ToOnboardingSettings,
} from '@modules/themes/utils';
import {
  FULLSCREEN_DIALOG_BUTTON_SX,
  FULLSCREEN_DIALOG_HEADER_SX,
  FULLSCREEN_DIALOG_PAPER_SX,
  FULLSCREEN_DIALOG_TITLE_SX,
} from '@modules/themes/components/fullscreenDialogChrome';

export const THEME_EDITOR_DIALOG_V2_TEST_IDS = {
  dialog: 'theme-editor-dialog-v2',
  back: 'v2-theme-back-btn',
  save: 'v2-theme-save-btn',
  done: 'v2-theme-done-btn',
} as const;

/**
 * Deep clone utility
 */
function deepClone<T>(obj: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(obj);
  }
  return JSON.parse(JSON.stringify(obj));
}

export interface ThemeEditorDialogV2Props {
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog is closed (Cancel) */
  onClose: () => void;
  /** Initial V2 theme settings to edit */
  themeSettings: OnboardingThemeSettingsV2;
  /** Callback when save is clicked - returns updated settings */
  onSave: (updated: OnboardingThemeSettingsV2) => Promise<void>;
  /** Title for dialog header */
  title?: string;
  /** Default language for screen editor */
  defaultLanguage?: string;
  /** Languages for screen editor */
  languages?: string[];
  /** Project ID for screen editor (asset upload) */
  projectId?: string;
  /** Onboarding ID for screen editor */
  onboardingId?: string;
  /** Version ID for screen editor */
  versionId?: string;
  /**
   * Opt-in: selective theme apply to live canvas screens.
   * Receives the current (unsaved) draft theme and the selected screen types.
   * Parent owns the apply dialog and persistence.
   */
  onApplyThemeToCanvas?: (payload: {
    theme: ThemeV2;
    screenTypes: string[];
    /** Per-kind template id (the clicked tile) for duplicate-kind themes. */
    templateIdByKind?: Record<string, string>;
  }) => void;
}

/**
 * Wrap StepNode as FlowStepNode for the V2 screen editor dialog.
 * FlowStepNode extends StepNode with `entityId` (= step.id).
 */
function toFlowStepNode(screenId: string, step: StepNode): FlowStepNode {
  return {
    ...step,
    id: screenId,
    entityId: screenId,
  } as FlowStepNode;
}

/**
 * ThemeEditorDialogV2 - Full-screen V2 theme editor for canvas
 */
export const ThemeEditorDialogV2: React.FC<ThemeEditorDialogV2Props> = ({
  open,
  onClose,
  themeSettings,
  onSave,
  title = 'Edit V2 Theme Settings',
  defaultLanguage = 'en-US',
  languages = ['en-US'],
  projectId = '',
  onboardingId = '',
  versionId = '',
  onApplyThemeToCanvas,
}) => {
  // Local theme state for editing
  const [localTheme, setLocalTheme] = useState<ThemeV2 | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [savingAction, setSavingAction] = useState<'save' | 'done' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  // Screen types selected for selective apply-to-canvas
  const [selectedScreenTypes, setSelectedScreenTypes] = useState<string[]>([]);

  // Screen editor dialog state
  const [screenEditorOpen, setScreenEditorOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<FlowStepNode | null>(null);

  // Key to force carousel refresh
  const [carouselKey, setCarouselKey] = useState(0);
  const saving = savingAction !== null;

  // Ref to store latest state for callbacks (avoids stale closures)
  const stateRef = useRef({
    localTheme,
    setLocalTheme,
    setCarouselKey,
    setScreenEditorOpen,
    setEditingStep,
  });

  // Initialize local theme when dialog opens
  useEffect(() => {
    if (open && themeSettings) {
      const initialTheme = onboardingSettingsV2ToThemeV2(themeSettings, {
        id: 'theme-editor-temp',
        name: 'Theme Settings',
      });
      setLocalTheme(initialTheme);
      setHasChanges(false);
      setSavingAction(null);
      setError(null);
      setGenerateOpen(false);
      // Default: every template tile selected for apply-to-canvas. Keyed by
      // tile (kind for un-subtyped, kind::subtype for variants) so two same-kind
      // subtyped tiles select independently.
      setSelectedScreenTypes(
        Array.from(
          new Set(
            Object.values(initialTheme.flowDocument?.screens ?? {}).map(
              (s) => templateTileKey(s.screenType, s.subtype)
            )
          )
        )
      );
    }
  }, [open, themeSettings]);

  // Keep ref updated
  useEffect(() => {
    stateRef.current = {
      localTheme,
      setLocalTheme,
      setCarouselKey,
      setScreenEditorOpen,
      setEditingStep,
    };
  });

  // Handle theme change from ThemeV2EditorView
  const handleThemeChange = useCallback((updatedTheme: ThemeV2) => {
    setLocalTheme(updatedTheme);
    setHasChanges(true);
  }, []);

  // Handle screen edit click from carousel
  const handleScreenEdit = useCallback((screenId: string, step: StepNode) => {
    setEditingStep(toFlowStepNode(screenId, step));
    setScreenEditorOpen(true);
  }, []);

  // Handle screen saved from V2 editor dialog
  const handleStepSaved = useMemo(() => {
    return (savedStep: FlowStepNode) => {
      if (!savedStep?.id) return;

      const state = stateRef.current;
      const currentTheme = state.localTheme;
      if (!currentTheme) return;

      // Update screen in theme's flowDocument
      const updatedTheme = deepClone(currentTheme);
      if (updatedTheme.flowDocument?.screens) {
        updatedTheme.flowDocument.screens[savedStep.id] = savedStep;
      }

      state.setLocalTheme(updatedTheme);
      setHasChanges(true);
      state.setCarouselKey(prev => prev + 1);
      state.setScreenEditorOpen(false);
      state.setEditingStep(null);
    };
  }, []);

  // Close screen editor
  const handleScreenEditorClose = useCallback(() => {
    setScreenEditorOpen(false);
    setEditingStep(null);
  }, []);

  const persistTheme = useCallback(async (action: 'save' | 'done'): Promise<boolean> => {
    if (!localTheme) return false;

    setSavingAction(action);
    setError(null);

    try {
      const updatedSettings = themeV2ToOnboardingSettings(localTheme, {
        sourceThemeId: themeSettings.sourceThemeId,
      });
      await onSave(updatedSettings);
      setHasChanges(false);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save V2 theme settings';
      setError(message);
      return false;
    } finally {
      setSavingAction(null);
    }
  }, [localTheme, themeSettings.sourceThemeId, onSave]);

  // Save persists the draft and keeps the editor open.
  const handleSave = useCallback(async () => {
    await persistTheme('save');
  }, [persistTheme]);

  // Done persists the draft and closes only after a successful save.
  const handleDone = useCallback(async () => {
    if (await persistTheme('done')) {
      onClose();
    }
  }, [persistTheme, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOpenGenerate = useCallback(() => {
    setError(null);
    setGenerateOpen(true);
  }, []);

  const handleToggleScreenType = useCallback((screenType: string) => {
    setSelectedScreenTypes((current) =>
      current.includes(screenType)
        ? current.filter((type) => type !== screenType)
        : [...current, screenType]
    );
  }, []);

  const handleApplyToCanvas = useCallback(() => {
    if (!localTheme || !onApplyThemeToCanvas || selectedScreenTypes.length === 0) return;
    onApplyThemeToCanvas({ theme: localTheme, screenTypes: selectedScreenTypes });
  }, [localTheme, onApplyThemeToCanvas, selectedScreenTypes]);

  const handleApplyForType = useCallback((screenType: string, templateScreenId?: string, subtype?: string) => {
    if (!localTheme || !onApplyThemeToCanvas) return;
    // Scope the apply to this tile's identity only (subtyped tiles never drag
    // their siblings in); templateIdByKind stays kind-keyed for the engine.
    onApplyThemeToCanvas({
      theme: localTheme,
      screenTypes: [templateTileKey(screenType, subtype)],
      ...(templateScreenId
        ? { templateIdByKind: { [screenType]: templateScreenId } }
        : {}),
    });
  }, [localTheme, onApplyThemeToCanvas]);

  const handleApplyGeneratedTheme = useCallback(async (draft: CreateThemeV2Input) => {
    setLocalTheme((currentTheme) => createEditableThemeV2FromDraft(draft, {
      baseTheme: currentTheme,
      fallbackId: 'theme-editor-temp',
      fallbackName: 'Theme Settings',
      preserveBaseName: true,
    }));
    setHasChanges(true);
    setCarouselKey((prev) => prev + 1);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullScreen
      sx={FULLSCREEN_DIALOG_PAPER_SX}
      data-testid={THEME_EDITOR_DIALOG_V2_TEST_IDS.dialog}
    >
      <Box component="header" sx={FULLSCREEN_DIALOG_HEADER_SX}>
        <Button
          size="small"
          onClick={handleCancel}
          disabled={saving}
          sx={{ ...FULLSCREEN_DIALOG_BUTTON_SX, zIndex: 1 }}
          data-testid={THEME_EDITOR_DIALOG_V2_TEST_IDS.back}
        >
          Back
        </Button>

        <Typography variant="h6" component="h2" noWrap sx={FULLSCREEN_DIALOG_TITLE_SX}>
          {title}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto', zIndex: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleSave}
            disabled={saving || !localTheme || !hasChanges}
            sx={FULLSCREEN_DIALOG_BUTTON_SX}
            data-testid={THEME_EDITOR_DIALOG_V2_TEST_IDS.save}
          >
            {savingAction === 'save' ? <CircularProgress size={16} color="inherit" /> : 'Save'}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleDone}
            disabled={saving || !localTheme}
            sx={FULLSCREEN_DIALOG_BUTTON_SX}
            data-testid={THEME_EDITOR_DIALOG_V2_TEST_IDS.done}
          >
            {savingAction === 'done' ? <CircularProgress size={16} color="inherit" /> : 'Done'}
          </Button>
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {localTheme && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {localTheme.generatedMetadata?.sourceKind === 'image-analysis' && (
              <Box sx={{ flexShrink: 0, mb: 2, overflow: 'hidden', borderRadius: 1 }}>
                <GeneratedThemeMetadataPanel theme={localTheme} />
              </Box>
            )}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ThemeV2EditorView
                theme={localTheme}
                onChange={handleThemeChange}
                onScreenEdit={handleScreenEdit}
                disabled={saving}
                carouselKey={carouselKey}
                projectId={projectId}
                selectable={Boolean(onApplyThemeToCanvas)}
                selectedScreenTypes={selectedScreenTypes}
                onToggleScreenType={handleToggleScreenType}
                onSetSelectedScreenTypes={setSelectedScreenTypes}
                onApplyForType={onApplyThemeToCanvas ? handleApplyForType : undefined}
                settingsPanelFooter={(projectId || onApplyThemeToCanvas) ? (
                  <Stack spacing={1}>
                    {onApplyThemeToCanvas && (
                      <>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={handleApplyToCanvas}
                          disabled={saving || selectedScreenTypes.length === 0}
                          fullWidth
                          data-testid="apply-theme-to-canvas-btn"
                          sx={{ justifyContent: 'flex-start' }}
                        >
                          Apply to canvas
                          {selectedScreenTypes.length > 0 ? ` (${selectedScreenTypes.length} type${selectedScreenTypes.length > 1 ? 's' : ''})` : ''}
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          Pushes the current draft (unsaved) theme onto existing canvas screens of the selected types.
                        </Typography>
                      </>
                    )}
                    {projectId && (
                      <Button
                        variant="outlined"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={handleOpenGenerate}
                        disabled={saving}
                        fullWidth
                        data-testid="v2-theme-generate-from-image-btn"
                        sx={{ justifyContent: 'flex-start' }}
                      >
                        Generate From Image
                      </Button>
                    )}
                  </Stack>
                ) : undefined}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Nested V2 screen editor dialog */}
      <OnboardingScreenEditorDialogV2
        open={screenEditorOpen}
        onClose={handleScreenEditorClose}
        stepData={editingStep}
        onStepSaved={handleStepSaved}
        defaultLanguage={defaultLanguage}
        languages={languages}
        projectId={projectId}
        onboardingId={onboardingId}
        versionId={versionId}
      />
      {projectId && localTheme && (
        <GenerateThemeFromImageDialog
          open={generateOpen}
          projectId={projectId}
          baseTheme={localTheme}
          createActionLabel="Apply to Editor"
          onClose={() => setGenerateOpen(false)}
          onCreateTheme={handleApplyGeneratedTheme}
        />
      )}
    </Dialog>
  );
};

export default ThemeEditorDialogV2;
